import { type FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import { type InputTableQuerySpec, type User } from "@/graphql.ts";
import { createContentBrowserTableSpec } from "@/redux/modules/content-browser.ts";
import {
  createUniqueKeyFromTableSpec,
  type ITableInitializationOptions,
  selectTable,
} from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { TABLE_CONTEXT_TYPE } from "@/settings/settings.js";

import DataTableContainer from "../core/grid/DataTableContainer.js";
import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";

interface IContentBrowserTableViewOwnProps {
  viewId?: number;
  selectedContentBrowserFeature: INavPanelCategory;
  selectedContentBrowserAttribute: IRouteNavItemProps;
  selectedEntityType?: string; // driving the User Group Admin context
  userGroupAdminContext?: boolean;
  getTableInitializationOptions: () => ITableInitializationOptions;
}

// required for sending all genomes (independent of usergroup assignment) in case of the admin page,
// values for context are defined in settings.ts -> TABLE_CONTEXT_TYPE
export interface ITableSpecWithContext extends InputTableQuerySpec {
  context?: string;
}

// for providing additional information into the ag-grid context (checking whether a genome is accessible for the admin)
export interface IUserGroupAdminContext {
  user: User;
}

/**
 * Control the Table in Content Browser
 *   Manages the initialization of the tables in case of new column definitions
 *   and changes to groupByAttribute or selectedEntryDiffers.
 *
 */
const ContentBrowserTableView: FC<IContentBrowserTableViewOwnProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const viewId = useURLViewID();
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);
  const tableSpec = useMemo(() => {
    const tableSpec = createContentBrowserTableSpec(props.selectedContentBrowserFeature?.eventKey);
    if (props.userGroupAdminContext) {
      tableSpec.context = TABLE_CONTEXT_TYPE.ADMIN;
    }
    return tableSpec;
  }, [props.selectedContentBrowserFeature?.eventKey, props.userGroupAdminContext]);

  useEffect(() => {
    dispatch(selectTable(tableSpec));
  }, [dispatch, tableSpec]);

  const userGroupAdminContext: IUserGroupAdminContext = { user: currentUser };

  return (
    <DataTableContainer
      key={`${createUniqueKeyFromTableSpec(tableSpec)}-${props.selectedContentBrowserAttribute.eventKey}`}
      tableSpec={tableSpec}
      viewId={viewId}
      tableInitializationOptions={props.getTableInitializationOptions()}
      gridContext={props.userGroupAdminContext && { userGroupAdminContext: userGroupAdminContext }}
    />
  );
};

export default ContentBrowserTableView;
